import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { Toaster, toast } from "react-hot-toast";
import { apiCall } from "utils/apiClient";
import { decryptCBCResponse } from "utils/decryptApi";
import {
  compressPortfolioVideo,
  formatFileSize,
  videoNeedsCompression,
} from "utils/compressPortfolioVideo";
import { compressPortfolioImage, imageNeedsCompression } from "utils/compressPortfolioImage";
import { FaArrowLeft, FaEdit, FaImage, FaSave, FaTimes, FaTrash, FaUpload } from "react-icons/fa";

const PROJECT_TYPES = ["Residential", "Commercial", "Retail"];
const API_BASE_URL = process.env.REACT_APP_HAPS_MAIN_BASE_URL || "http://localhost:8080/";
const emptyDetails = () => ({ name: "", location: "", type: "Residential", status: "active" });
const isSuccess = (result) => result?.status >= 200 && result?.status < 300;
const mediaUrl = (path) => {
  if (!path || /^(https?:|blob:|data:)/i.test(path)) return path || "";
  return `${API_BASE_URL.replace(/\/$/, "")}/${String(path).replace(/^\//, "")}`;
};

function PortfolioProjectPage({ mode }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isCreate = mode === "create";
  const isEdit = mode === "edit";
  const [project, setProject] = useState(null);
  const [details, setDetails] = useState(emptyDetails);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [existingMedia, setExistingMedia] = useState([]);
  const [removedMediaIds, setRemovedMediaIds] = useState([]);
  const [newMedia, setNewMedia] = useState([]);
  const [editingMediaId, setEditingMediaId] = useState(null);
  const [editingMediaName, setEditingMediaName] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadState, setUploadState] = useState("idle");
  const [compressing, setCompressing] = useState(false);
  const fileInputRef = useRef(null);
  const previewUrlsRef = useRef([]);

  const loadProject = async () => {
    if (isCreate) return;
    setLoading(true);
    const result = await apiCall({ endpoint: `portfolio/projects/${id}`, method: "GET" });
    if (isSuccess(result)) {
      setProject(result.data);
      setDetails({
        name: result.data.name || "",
        location: result.data.location || "",
        type: result.data.type || "Residential",
        status: result.data.status || "active",
      });
    } else {
      toast.error(result?.message || "Unable to load portfolio project.");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadProject();
  }, [id, mode]);
  useEffect(() => () => previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url)), []);

  const submitDetails = async (event) => {
    event.preventDefault();
    if (!details.name.trim() || !details.location.trim() || !details.type.trim()) {
      toast.error("Name, location and type are required.");
      return;
    }
    setSaving(true);
    const result = await apiCall({
      endpoint: isCreate ? "portfolio/projects" : `portfolio/projects/${id}/update`,
      method: "POST",
      data: {
        name: details.name.trim(),
        location: details.location.trim(),
        type: details.type.trim(),
        status: details.status,
      },
    });
    setSaving(false);
    if (!isSuccess(result)) {
      toast.error(result?.message || "Unable to save project.");
      return;
    }
    toast.success(isCreate ? "Project created successfully." : "Project updated successfully.");
    navigate(`/portfolio-management/${result.data?.id || id}`);
  };

  const openMediaManager = () => {
    setExistingMedia((project?.media || []).map((item) => ({ ...item, originalName: item.name })));
    setRemovedMediaIds([]);
    setNewMedia([]);
    setMediaOpen(true);
    window.setTimeout(
      () =>
        document.getElementById("portfolio-media-manager")?.scrollIntoView({ behavior: "smooth" }),
      0
    );
  };

  const closeMediaManager = () => {
    newMedia.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    setNewMedia([]);
    setRemovedMediaIds([]);
    setMediaOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const addFiles = async (event) => {
    setUploadProgress(0);
    setUploadState("idle");
    const files = Array.from(event.target.files || []);
    const valid = files.filter(
      (file) => file.type.startsWith("image/") || file.type.startsWith("video/")
    );
    if (valid.length !== files.length)
      toast.error("Only supported image and video files are allowed.");
    const selectedItems = valid.map((file) => {
      const previewUrl = URL.createObjectURL(file);
      previewUrlsRef.current.push(previewUrl);
      const type = file.type.startsWith("video/") ? "video" : "image";
      return {
        id: `${Date.now()}-${file.name}-${Math.random()}`,
        file,
        name: file.name,
        type,
        previewUrl,
        originalSize: file.size,
        requiredSizeCompression: videoNeedsCompression(file),
        compressionState: type === "video" || imageNeedsCompression(file) ? "pending" : "ready",
        compressionProgress: 0,
        compressionError: "",
      };
    });
    setNewMedia((current) => [...current, ...selectedItems]);
    event.target.value = "";

    const mediaToCompress = selectedItems.filter((item) => item.compressionState === "pending");
    if (!mediaToCompress.length) return;

    setCompressing(true);
    for (const item of mediaToCompress) {
      setNewMedia((items) =>
        items.map((media) =>
          media.id === item.id ? { ...media, compressionState: "compressing" } : media
        )
      );
      try {
        const compress = item.type === "video" ? compressPortfolioVideo : compressPortfolioImage;
        const compressedFile = await compress(item.file, (progress) => {
          setNewMedia((items) =>
            items.map((media) =>
              media.id === item.id ? { ...media, compressionProgress: progress } : media
            )
          );
        });
        const compressedPreviewUrl = URL.createObjectURL(compressedFile);
        previewUrlsRef.current.push(compressedPreviewUrl);
        URL.revokeObjectURL(item.previewUrl);
        setNewMedia((items) =>
          items.map((media) =>
            media.id === item.id
              ? {
                  ...media,
                  file: compressedFile,
                  previewUrl: compressedPreviewUrl,
                  compressionState: "compressed",
                  compressionProgress: 100,
                }
              : media
          )
        );
      } catch (error) {
        setNewMedia((items) =>
          items.map((media) =>
            media.id === item.id
              ? {
                  ...media,
                  compressionState: "error",
                  compressionError: error.message || "Video compression failed.",
                }
              : media
          )
        );
        toast.error(`${item.file.name}: ${error.message || "Media compression failed."}`);
      }
    }
    setCompressing(false);
  };

  const saveMedia = async () => {
    const kept = existingMedia.filter((item) => !removedMediaIds.includes(Number(item.id)));
    if (kept.some((item) => !item.name.trim()) || newMedia.some((item) => !item.name.trim())) {
      toast.error("Please give every media file a name.");
      return;
    }
    const existingChanged =
      removedMediaIds.length > 0 || existingMedia.some((item) => item.name !== item.originalName);
    if (!existingChanged && newMedia.length === 0) {
      toast("No media changes to save.");
      return;
    }
    setSaving(true);
    let result;
    if (existingChanged) {
      const payload = new FormData();
      payload.append(
        "existing_media_names",
        JSON.stringify(Object.fromEntries(kept.map((item) => [item.id, item.name.trim()])))
      );
      payload.append("removed_media_ids", JSON.stringify(removedMediaIds));
      newMedia.forEach((item) => {
        payload.append("new_media[]", item.file);
        payload.append("new_media_names[]", item.name.trim());
      });
      result = await apiCall({
        endpoint: `portfolio/projects/${id}/update`,
        method: "POST",
        data: payload,
      });
    } else {
      const payload = new FormData();
      newMedia.forEach((item) => {
        payload.append("media[]", item.file);
        payload.append("media_names[]", item.name.trim());
      });
      result = await apiCall({
        endpoint: `portfolio/projects/${id}/media`,
        method: "POST",
        data: payload,
      });
    }
    setSaving(false);
    if (!isSuccess(result)) {
      toast.error(result?.message || "Unable to save media.");
      return;
    }
    toast.success("Project media updated.");
    closeMediaManager();
    await loadProject();
  };

  const updateOneMedia = async (mediaId) => {
    const targetMediaId = Number(mediaId);
    if (!targetMediaId) {
      toast.error("Invalid media ID.");
      return;
    }
    if (!editingMediaName.trim()) {
      toast.error("Media name is required.");
      return;
    }
    setSaving(true);
    const payload = new FormData();
    payload.append(
      "existing_media_names",
      JSON.stringify({ [targetMediaId]: editingMediaName.trim() })
    );
    payload.append("removed_media_ids", "[]");
    const result = await apiCall({
      endpoint: `portfolio/projects/${id}/update`,
      method: "POST",
      data: payload,
    });
    setSaving(false);
    if (!isSuccess(result)) {
      toast.error(result?.message || "Unable to update media.");
      return;
    }
    toast.success("Media name updated.");
    setEditingMediaId(null);
    await loadProject();
  };

  const deleteOneMedia = async (item) => {
    const targetMediaId = Number(item.id);
    if (!targetMediaId) {
      toast.error("Invalid media ID.");
      return;
    }
    if (!window.confirm(`Remove “${item.name}” from this project?`)) return;
    setSaving(true);
    const payload = new FormData();
    payload.append("removed_media_ids", JSON.stringify([targetMediaId]));
    const result = await apiCall({
      endpoint: `portfolio/projects/${id}/update`,
      method: "POST",
      data: payload,
    });
    setSaving(false);
    if (!isSuccess(result)) {
      toast.error(result?.message || "Unable to remove media.");
      return;
    }
    toast.success("Media removed.");
    await loadProject();
  };

  const uploadSelectedMedia = async () => {
    if (!newMedia.length) {
      toast.error("Choose at least one image or video.");
      return;
    }
    if (newMedia.some((item) => !item.name.trim())) {
      toast.error("Please give every selected media file a name.");
      return;
    }
    if (compressing || newMedia.some((item) => item.compressionState === "compressing")) {
      toast.error("Please wait for media compression to finish.");
      return;
    }
    if (newMedia.some((item) => item.compressionState === "error")) {
      toast.error("Remove the file that could not be compressed before uploading.");
      return;
    }
    const payload = new FormData();
    newMedia.forEach((item) => {
      payload.append("media[]", item.file);
      payload.append("media_names[]", item.name.trim());
    });
    setUploading(true);
    setUploadState("uploading");
    setUploadProgress(0);
    const result = await new Promise((resolve) => {
      const request = new XMLHttpRequest();
      request.open("POST", `${API_BASE_URL.replace(/\/$/, "")}/portfolio/projects/${id}/media`);
      const token = localStorage.getItem("Token");
      if (token) request.setRequestHeader("Authorization", `Bearer ${token}`);
      request.upload.onprogress = (event) => {
        if (event.lengthComputable)
          setUploadProgress(Math.round((event.loaded / event.total) * 100));
      };
      request.onload = () => {
        try {
          resolve(decryptCBCResponse(JSON.parse(request.responseText)));
        } catch (error) {
          resolve({ status: request.status || 500, message: error.message, data: null });
        }
      };
      request.onerror = () => resolve({ status: 500, message: "Network error", data: null });
      request.send(payload);
    });
    setUploading(false);
    if (!isSuccess(result)) {
      setUploadState("error");
      toast.error(result?.message || "Unable to upload media.");
      return;
    }
    setUploadProgress(100);
    setUploadState("success");
    toast.success("Media uploaded successfully.");
    newMedia.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    setNewMedia([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
    await loadProject();
  };

  const deleteProject = async () => {
    if (!window.confirm(`Delete “${project.name}” and all its media files?`)) return;
    const result = await apiCall({ endpoint: `portfolio/projects/${id}`, method: "DELETE" });
    if (!isSuccess(result)) {
      toast.error(result?.message || "Unable to delete project.");
      return;
    }
    toast.success("Portfolio project deleted.");
    navigate("/portfolio-management");
  };

  if (loading)
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div className="py-16 text-center text-slate-500">Loading project...</div>
      </DashboardLayout>
    );
  if (!isCreate && !project)
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div className="py-16 text-center">
          <p className="text-slate-600">Project could not be loaded.</p>
          <button
            onClick={() => navigate("/portfolio-management")}
            className="mt-4 font-bold text-indigo-600"
          >
            Back to portfolio
          </button>
        </div>
      </DashboardLayout>
    );

  const formPage = isCreate || isEdit;
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Toaster position="top-center" />
      <div className="space-y-3 py-3">
        <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
          <button
            onClick={() =>
              navigate(isEdit ? `/portfolio-management/${id}` : "/portfolio-management")
            }
            className="inline-flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-indigo-600"
          >
            <FaArrowLeft /> Back
          </button>
          {!formPage && (
            <div className="flex gap-2">
              <button
                onClick={() => navigate(`/portfolio-management/${id}/edit`)}
                className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700"
              >
                <FaEdit /> Edit Project
              </button>
              <button
                onClick={deleteProject}
                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-700"
              >
                <FaTrash /> Delete Project
              </button>
            </div>
          )}
        </div>

        {formPage ? (
          <form
            onSubmit={submitDetails}
            className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200"
          >
            <div>
              <h1 className="text-xl font-bold text-slate-900">
                {isCreate ? "Add New Project" : "Edit Project"}
              </h1>
              <p className="mt-1 text-xs text-slate-500">
                All required project details are saved separately from media.
              </p>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <label className="text-sm font-semibold text-slate-700">
                Project name *
                <input
                  value={details.name}
                  onChange={(event) => setDetails({ ...details, name: event.target.value })}
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal outline-none focus:border-indigo-500"
                />
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Location *
                <input
                  value={details.location}
                  onChange={(event) => setDetails({ ...details, location: event.target.value })}
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal outline-none focus:border-indigo-500"
                />
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Project type *
                <select
                  value={details.type}
                  onChange={(event) => setDetails({ ...details, type: event.target.value })}
                  className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal"
                >
                  {PROJECT_TYPES.map((type) => (
                    <option key={type}>{type}</option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Status
                <select
                  value={details.status}
                  onChange={(event) => setDetails({ ...details, status: event.target.value })}
                  className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-3">
              <button
                type="button"
                onClick={() =>
                  navigate(isEdit ? `/portfolio-management/${id}` : "/portfolio-management")
                }
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700"
              >
                Cancel
              </button>
              <button
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-60"
              >
                <FaSave /> {saving ? "Saving..." : isCreate ? "Create Project" : "Save Changes"}
              </button>
            </div>
          </form>
        ) : (
          <>
            <section className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
              <div className="border-b border-slate-100 px-4 py-3">
                <h1 className="text-lg font-bold text-slate-900">{project.name}</h1>
                <p className="text-xs text-slate-500">Project #{project.id}</p>
              </div>
              <div className="grid divide-y divide-slate-100 sm:grid-cols-4 sm:divide-x sm:divide-y-0">
                {[
                  ["Location", project.location],
                  ["Project type", project.type],
                  ["Status", project.status],
                  ["Media", `${project.media?.length || 0} files`],
                ].map(([label, value]) => (
                  <div key={label} className="px-4 py-3">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                      {label}
                    </p>
                    <p
                      className={`mt-1 text-sm font-bold capitalize ${
                        label === "Status" && value === "active"
                          ? "text-emerald-600"
                          : "text-slate-900"
                      }`}
                    >
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            </section>
            <section className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
              <div className="flex flex-col justify-between gap-2 border-b border-slate-100 pb-3 sm:flex-row sm:items-center">
                <div>
                  <h2 className="text-base font-bold text-slate-900">Project Media</h2>
                  <p className="text-xs text-slate-500">
                    {project.media?.length || 0} uploaded files
                  </p>
                </div>
              </div>
              {project.media?.length ? (
                <div className="mt-3 grid gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                  {project.media.map((item) => (
                    <article
                      key={item.id}
                      className="overflow-hidden rounded-lg border border-slate-200 bg-white"
                    >
                      <div className="aspect-video bg-slate-900">
                        {item.type === "video" ? (
                          <video
                            src={mediaUrl(item.file_path)}
                            controls
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <img
                            src={mediaUrl(item.thumbnail_path || item.file_path)}
                            alt={item.name}
                            className="h-full w-full object-cover"
                          />
                        )}
                      </div>
                      <div className="px-2.5 py-2">
                        {editingMediaId === item.id ? (
                          <div className="space-y-2">
                            <input
                              value={editingMediaName}
                              onChange={(event) => setEditingMediaName(event.target.value)}
                              className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs"
                              autoFocus
                            />
                            <div className="flex gap-1.5">
                              <button
                                disabled={saving}
                                onClick={() => updateOneMedia(item.id)}
                                className="flex-1 rounded-md bg-indigo-600 px-2 py-1.5 text-[11px] font-bold text-white"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingMediaId(null)}
                                className="rounded-md bg-slate-100 px-2 py-1.5 text-[11px] font-bold text-slate-600"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-2">
                            <p className="min-w-0 flex-1 truncate text-xs font-bold text-slate-800">
                              {item.name}
                            </p>
                            <div className="flex gap-1">
                              <button
                                title="Edit media name"
                                onClick={() => {
                                  setEditingMediaId(item.id);
                                  setEditingMediaName(item.name);
                                }}
                                className="rounded-md bg-indigo-50 p-1.5 text-indigo-600"
                              >
                                <FaEdit />
                              </button>
                              <button
                                title="Delete media"
                                onClick={() => deleteOneMedia(item)}
                                className="rounded-md bg-red-50 p-1.5 text-red-600"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="mt-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 py-6 text-center">
                  <FaImage className="mx-auto text-2xl text-slate-300" />
                  <p className="mt-2 text-sm font-bold text-slate-600">No media uploaded yet</p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-2 text-xs font-bold text-indigo-600"
                  >
                    Upload the first media files
                  </button>
                </div>
              )}
              <div className="mt-3 border-t border-slate-100 pt-3">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                  <label
                    htmlFor="portfolio-direct-upload"
                    className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-indigo-300 bg-indigo-50 px-4 py-3 text-xs font-bold text-indigo-700"
                  >
                    <FaUpload /> Choose Media
                  </label>
                  <input
                    ref={fileInputRef}
                    id="portfolio-direct-upload"
                    type="file"
                    multiple
                    disabled={compressing || uploading}
                    accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
                    onChange={addFiles}
                    className="hidden"
                  />
                  <p className="text-xs text-slate-500">
                    Images over 5 MB are compressed · all videos are watermarked and videos over 20
                    MB are also compressed
                  </p>
                </div>
                {newMedia.length > 0 && (
                  <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {newMedia.map((item) => (
                      <div
                        key={item.id}
                        className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 p-2"
                      >
                        {item.type === "video" ? (
                          <video
                            src={item.previewUrl}
                            className="h-20 w-28 shrink-0 rounded-md object-cover"
                            muted
                          />
                        ) : (
                          <img
                            src={item.previewUrl}
                            alt=""
                            className="h-20 w-28 shrink-0 rounded-md object-cover"
                          />
                        )}
                        <input
                          value={item.name}
                          onChange={(event) =>
                            setNewMedia((items) =>
                              items.map((media) =>
                                media.id === item.id
                                  ? { ...media, name: event.target.value }
                                  : media
                              )
                            )
                          }
                          placeholder="Media name *"
                          className="min-w-0 flex-1 rounded-md border border-slate-300 px-2 py-1.5 text-xs"
                        />
                        <button
                          onClick={() =>
                            setNewMedia((items) => items.filter((media) => media.id !== item.id))
                          }
                          className="p-1.5 text-red-600"
                        >
                          <FaTimes />
                        </button>
                        {item.type && (
                          <div className="w-full text-[10px] font-semibold text-slate-500">
                            {item.compressionState === "compressing" && (
                              <span className="text-indigo-600">
                                Compressing {item.compressionProgress}%
                              </span>
                            )}
                            {item.compressionState === "compressed" && (
                              <span className="text-emerald-600">
                                {item.type === "video"
                                  ? item.requiredSizeCompression
                                    ? "Compressed + watermarked"
                                    : "Watermarked"
                                  : "Compressed"}{" "}
                                {formatFileSize(item.originalSize)} →{" "}
                                {formatFileSize(item.file.size)}
                              </span>
                            )}
                            {item.compressionState === "ready" && (
                              <span>{formatFileSize(item.file.size)} · ready</span>
                            )}
                            {item.compressionState === "error" && (
                              <span className="text-red-600">{item.compressionError}</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {uploadState !== "idle" && (
                  <div className="mt-3">
                    <div className="mb-1 flex justify-between text-xs font-bold text-slate-600">
                      <span className={uploadState === "error" ? "text-red-600" : "text-slate-600"}>
                        {uploadState === "uploading"
                          ? uploadProgress === 100
                            ? "Upload sent, waiting for server..."
                            : "Uploading media..."
                          : uploadState === "success"
                          ? "Upload successful"
                          : "Upload failed — fix the issue and retry"}
                      </span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                      <div
                        className={`h-full transition-all ${
                          uploadState === "error"
                            ? "bg-red-500"
                            : uploadState === "success"
                            ? "bg-emerald-500"
                            : "bg-indigo-600"
                        }`}
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}
                {newMedia.length > 0 && (
                  <div className="mt-3 flex justify-end">
                    <button
                      disabled={uploading || compressing}
                      onClick={uploadSelectedMedia}
                      className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-bold text-white disabled:opacity-60"
                    >
                      <FaUpload />{" "}
                      {compressing
                        ? "Compressing media..."
                        : uploading
                        ? `Uploading ${uploadProgress}%`
                        : `Upload ${newMedia.length} File${newMedia.length > 1 ? "s" : ""}`}
                    </button>
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </div>

      {false && mediaOpen && (
        <section
          id="portfolio-media-manager"
          className="mb-4 rounded-xl bg-white p-4 shadow-sm ring-1 ring-indigo-200"
        >
          <div className="flex items-start justify-between border-b border-slate-100 pb-3">
            <div>
              <h2 className="text-base font-bold text-slate-900">Manage Project Media</h2>
              <p className="mt-1 text-xs text-slate-500">
                Upload new files, rename old media, or remove unwanted files.
              </p>
            </div>
            <button
              onClick={closeMediaManager}
              className="rounded-lg bg-slate-100 p-2 text-slate-600"
            >
              <FaTimes />
            </button>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {existingMedia
              .filter((item) => !removedMediaIds.includes(Number(item.id)))
              .map((item) => (
                <div key={item.id} className="overflow-hidden rounded-lg border border-slate-200">
                  <div className="relative aspect-video bg-slate-900">
                    {item.type === "video" ? (
                      <video
                        src={mediaUrl(item.file_path)}
                        controls
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <img
                        src={mediaUrl(item.thumbnail_path || item.file_path)}
                        alt={item.name}
                        className="h-full w-full object-cover"
                      />
                    )}
                    <button
                      onClick={() => setRemovedMediaIds((ids) => [...ids, Number(item.id)])}
                      className="absolute right-2 top-2 rounded-full bg-red-600 p-2 text-xs text-white"
                      title="Remove media"
                    >
                      <FaTrash />
                    </button>
                  </div>
                  <div className="p-2">
                    <label className="text-xs font-bold text-slate-500">Media name</label>
                    <input
                      value={item.name}
                      onChange={(event) =>
                        setExistingMedia((items) =>
                          items.map((media) =>
                            media.id === item.id ? { ...media, name: event.target.value } : media
                          )
                        )
                      }
                      className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs"
                    />
                  </div>
                </div>
              ))}
            {newMedia.map((item) => (
              <div key={item.id} className="overflow-hidden rounded-lg border border-indigo-200">
                <div className="relative aspect-video bg-slate-900">
                  {item.type === "video" ? (
                    <video src={item.previewUrl} controls className="h-full w-full object-cover" />
                  ) : (
                    <img
                      src={item.previewUrl}
                      alt="New upload"
                      className="h-full w-full object-cover"
                    />
                  )}
                  <button
                    onClick={() =>
                      setNewMedia((items) => items.filter((media) => media.id !== item.id))
                    }
                    className="absolute right-2 top-2 rounded-full bg-red-600 p-2 text-xs text-white"
                  >
                    <FaTimes />
                  </button>
                </div>
                <div className="p-2">
                  <label className="text-xs font-bold text-indigo-600">New media name *</label>
                  <input
                    value={item.name}
                    onChange={(event) =>
                      setNewMedia((items) =>
                        items.map((media) =>
                          media.id === item.id ? { ...media, name: event.target.value } : media
                        )
                      )
                    }
                    className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs"
                  />
                </div>
              </div>
            ))}
          </div>
          {removedMediaIds.length > 0 && (
            <button
              onClick={() => setRemovedMediaIds([])}
              className="mt-4 text-xs font-bold text-indigo-600"
            >
              Undo {removedMediaIds.length} removal(s)
            </button>
          )}
          <div className="mt-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 text-center">
            <input
              ref={fileInputRef}
              id="portfolio-media-upload"
              type="file"
              multiple
              disabled={compressing || uploading}
              accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
              onChange={addFiles}
              className="hidden"
            />
            <label htmlFor="portfolio-media-upload" className="cursor-pointer">
              <FaUpload className="mx-auto text-base text-indigo-600" />
              <span className="mt-1 block text-xs font-bold text-slate-800">
                Choose images or videos
              </span>
              <span className="mt-1 block text-xs text-slate-500">
                Images up to 5 MB · Videos up to 20 MB
              </span>
            </label>
          </div>
          <div className="mt-3 flex justify-end gap-2 border-t border-slate-100 pt-3">
            <button
              onClick={closeMediaManager}
              className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700"
            >
              Cancel
            </button>
            <button
              disabled={saving}
              onClick={saveMedia}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-bold text-white disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save Media Changes"}
            </button>
          </div>
        </section>
      )}
    </DashboardLayout>
  );
}

PortfolioProjectPage.propTypes = {
  mode: PropTypes.oneOf(["create", "view", "edit"]).isRequired,
};

export default PortfolioProjectPage;
